export interface ExamPaper {
  id?: number
  title: string
  year: number
  region: string
  subject: string
  difficulty?: string
  tags?: string[]
  duration?: number
  suggestion?: string
  description?: string
  file_key: string
  file_size?: number
  views?: number
  downloads?: number
  created_at?: string
  updated_at?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  total: number
  page: number
  per_page: number
  total_pages: number
}

interface DistributionItem {
  name: string
  value: number
}

interface TopPaper {
  id: number
  title: string
  year: number
  subject: string
  count: number
}

export interface Statistics {
  overview: {
    total_papers: number
    total_views: number
    total_downloads: number
    total_storage: number
    papers_this_month: number
  }
  by_year: DistributionItem[]
  by_subject: DistributionItem[]
  by_region: DistributionItem[]
  by_difficulty: DistributionItem[]
  top_viewed: TopPaper[]
  top_downloaded: TopPaper[]
  tag_stats: DistributionItem[]
  recent_uploads: {
    id: number
    title: string
    subject: string
    created_at: string
  }[]
}

class ApiClient {
  private baseUrl = '/api'

  // Function to get auth token - will be set by Clerk
  private getAuthToken: (() => Promise<string | null>) | null = null
  private onAuthError: (() => void) | null = null

  setAuthTokenGetter(getter: () => Promise<string | null>) {
    this.getAuthToken = getter
  }

  setAuthErrorHandler(handler: () => void) {
    this.onAuthError = handler
  }

  private async getAuthHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {}
    if (this.getAuthToken) {
      const token = await this.getAuthToken()
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }
    }
    return headers
  }

  private async handleResponse<T>(response: Response, errorMessage: string): Promise<T> {
    if (!response.ok) {
      // Handle authentication errors
      if (response.status === 401) {
        if (this.onAuthError) {
          this.onAuthError()
        }
        throw new Error('认证失败，请重新登录')
      }

      // Handle authorization errors (forbidden)
      if (response.status === 403) {
        throw new Error('权限不足，无法执行此操作')
      }

      // Handle other errors
      try {
        const error = await response.json() as { error?: string }
        throw new Error(error.error || errorMessage)
      } catch {
        throw new Error(errorMessage)
      }
    }

    return response.json()
  }

  async getPapers(params?: {
    page?: number
    per_page?: number
    year?: string
    subject?: string
    search?: string
  }): Promise<PaginatedResponse<ExamPaper>> {
    const searchParams = new URLSearchParams()
    if (params?.page) searchParams.append('page', params.page.toString())
    if (params?.per_page) searchParams.append('per_page', params.per_page.toString())
    if (params?.year) searchParams.append('year', params.year)
    if (params?.subject) searchParams.append('subject', params.subject)
    if (params?.search) searchParams.append('search', params.search)

    const response = await fetch(`${this.baseUrl}/papers?${searchParams}`)
    return this.handleResponse<PaginatedResponse<ExamPaper>>(response, '加载试卷失败')
  }

  async getPaper(id: number): Promise<ExamPaper> {
    const response = await fetch(`${this.baseUrl}/papers/${id}`)
    return this.handleResponse<ExamPaper>(response, '加载试卷详情失败')
  }

  async createPaper(paper: Omit<ExamPaper, 'id' | 'views' | 'downloads' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; id: number }> {
    const authHeaders = await this.getAuthHeaders()
    const response = await fetch(`${this.baseUrl}/papers`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: JSON.stringify(paper)
    })
    return this.handleResponse<{ success: boolean; id: number }>(response, '创建试卷失败')
  }

  async updatePaper(id: number, updates: Partial<ExamPaper>): Promise<{ success: boolean }> {
    const authHeaders = await this.getAuthHeaders()
    const response = await fetch(`${this.baseUrl}/papers/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: JSON.stringify(updates)
    })
    return this.handleResponse<{ success: boolean }>(response, '更新试卷失败')
  }

  async deletePaper(id: number): Promise<{ success: boolean }> {
    const authHeaders = await this.getAuthHeaders()
    const response = await fetch(`${this.baseUrl}/papers/${id}`, {
      method: 'DELETE',
      headers: authHeaders
    })
    return this.handleResponse<{ success: boolean }>(response, '删除试卷失败')
  }

  async uploadFile(file: File): Promise<{ success: boolean; file_key: string; file_size: number }> {
    const formData = new FormData()
    formData.append('file', file)

    const authHeaders = await this.getAuthHeaders()
    const response = await fetch(`${this.baseUrl}/upload`, {
      method: 'POST',
      headers: authHeaders,
      body: formData
    })
    return this.handleResponse<{ success: boolean; file_key: string; file_size: number }>(response, '上传文件失败')
  }

  getDownloadUrl(id: number): string {
    return `${this.baseUrl}/download/${id}.pdf`
  }

  getPreviewUrl(id: number): string {
    return `${this.baseUrl}/preview/${id}`
  }

  async getStatistics(): Promise<Statistics> {
    const response = await fetch(`${this.baseUrl}/statistics`)
    return this.handleResponse<Statistics>(response, '加载统计数据失败')
  }

  async batchUploadFiles(files: File[]): Promise<{
    success: boolean
    results: Array<{
      filename: string
      success: boolean
      file_key?: string
      file_size?: number
      error?: string
    }>
    summary: {
      total: number
      successful: number
      failed: number
    }
  }> {
    const formData = new FormData()
    files.forEach((file, index) => {
      formData.append(`file_${index}`, file)
    })

    const authHeaders = await this.getAuthHeaders()
    const response = await fetch(`${this.baseUrl}/batch-upload`, {
      method: 'POST',
      headers: authHeaders,
      body: formData
    })

    return this.handleResponse<{
      success: boolean
      results: Array<{
        filename: string
        success: boolean
        file_key?: string
        file_size?: number
        error?: string
      }>
      summary: {
        total: number
        successful: number
        failed: number
      }
    }>(response, '批量上传失败')
  }

  async batchCreatePapers(papers: Array<Omit<ExamPaper, 'id' | 'views' | 'downloads' | 'created_at' | 'updated_at'>>): Promise<{
    success: boolean
    results: Array<{
      success: boolean
      id?: number
      error?: string
    }>
    summary: {
      total: number
      successful: number
      failed: number
    }
  }> {
    const authHeaders = await this.getAuthHeaders()
    const response = await fetch(`${this.baseUrl}/papers/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders
      },
      body: JSON.stringify({ papers })
    })

    return this.handleResponse<{
      success: boolean
      results: Array<{
        success: boolean
        id?: number
        error?: string
      }>
      summary: {
        total: number
        successful: number
        failed: number
      }
    }>(response, '批量创建试卷失败')
  }
}

export const api = new ApiClient()
